import React, { useEffect } from 'react';
import { X, Printer, Download } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface RefereeSheetModalProps {
  isOpen: boolean;
  html: string | null;
  onClose: () => void;
}

export function RefereeSheetModal({ isOpen, html, onClose }: RefereeSheetModalProps) {
  const { t } = useTranslation();

  // Fermer avec Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Gérer l'impression
  const handlePrint = () => {
    if (!html) return;

    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      
      // Attendre que le contenu soit chargé puis imprimer
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }
  };

  // Gérer le téléchargement HTML
  const handleDownload = () => {
    if (!html) return;

    try {
      // Créer un blob avec le HTML
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      
      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = url;
      link.download = 'referee-sheet.html';
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Nettoyer l'URL
      URL.revokeObjectURL(url);
      
      console.log('✅ Referee sheet downloaded');
    } catch (error) {
      console.error('❌ Error downloading sheet:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header avec boutons d'action */}
        <div className="flex items-center justify-between p-4 border-b bg-white shrink-0">
          <h2 className="text-lg font-bold text-gray-900">
            {t('screens.refereeSheet.title')}
          </h2>
          
          <div className="flex items-center gap-2">
            {/* Bouton Print */}
            <button
              onClick={handlePrint}
              className="bg-blue-500 text-white px-3 py-2 font-semibold hover:bg-blue-600 flex items-center gap-2 text-sm"
            >
              <Printer size={16} />
              {t('screens.refereeSheet.actions.print')}
            </button>
            
            {/* Bouton Download */}
            <button
              onClick={handleDownload}
              className="bg-green-500 text-white px-3 py-2 font-semibold hover:bg-green-600 flex items-center gap-2 text-sm"
            >
              <Download size={16} />
              {t('screens.refereeSheet.actions.download')}
            </button>
            
            {/* Bouton Close */}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Contenu de la feuille (scrollable) */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
          {html ? (
            <div 
              className="bg-white mx-auto max-w-4xl"
              style={{ minHeight: '297mm' }} // Hauteur A4 approximative
              dangerouslySetInnerHTML={{ __html: html }}
            />
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">
                {t('screens.refereeSheet.noContent')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}